<template>
    <div class="datepickerContainer text-center w-100">

        <div class="filter_section">
            <label class="filter_label">{{ $t('startDate') }}</label>
            <input type="date" v-model="startDate" class="filter_input" />

            <label class="filter_label">{{ $t('endDate') }}</label>
            <input type="date" v-model="endDate" class="filter_input" />

            <div class="time_range_row">
                <div class="time_input_group">
                    <label class="filter_label">{{ $t('start') }}:</label>
                    <input type="time" v-model="startTime" class="filter_input" />
                </div>

                <div class="time_input_group">
                    <label class="filter_label">{{ $t('end') }}:</label>
                    <input type="time" v-model="endTime" class="filter_input" />
                </div>
            </div>


        </div>


        <!-- Quick ranges bar (like your image) -->
        <div class="quick_ranges_row">
            <button class="quick_button" @click="applyQuickRange('lastMonth')">
                {{ $t('lastMonth') || 'Último mês' }}
            </button>
            <button class="quick_button" @click="applyQuickRange('lastWeek')">
                {{ $t('lastWeek') || 'Última semana' }}
            </button>
            <button class="quick_button" @click="applyQuickRange('todayFull')">
                {{ $t('today') || 'Hoje' }}
            </button>
            <button class="quick_button" @click="applyQuickRange('lastHour')">
                {{ $t('lastHour') || '1 hora' }}
            </button>
            <button class="quick_button" @click="applyQuickRange('last30Minutes')">
                {{ $t('last30Minutes') || '30 minutos' }}
            </button>
        </div>

        <div class="button_row">
            <button class="utility_button" @click="clear">
                {{ $t('clear') }}
            </button>
        </div>

        <i>{{ $t('selectedDays') }}: {{ label }} </i>
    </div>
</template>
  
<script>
export default {
    name: 'DateTimeRangePicker',

    props: {
        value: {
            type: Array,
            required: false,
        }
    },

    data() {
        return {
            startDate: '',
            endDate: '',
            startTime: '00:00',
            endTime: '23:59',
        }
    },

    computed: {
        label() {
            const formatDateTime = (dateStr, timeStr) => {
                if (!dateStr || !timeStr) return '';
                const [year, month, day] = dateStr.split('-');
                return `${day}/${month}/${year} ${timeStr}`;
            };

            const start = formatDateTime(this.startDate, this.startTime);
            const end = formatDateTime(this.endDate, this.endTime);

            return start === end ? start : `${start} até ${end}`;
        }
    },

    methods: {
        emitChange() {
            if (this.startDate && this.endDate && this.startTime && this.endTime) {
                const start = new Date(`${this.startDate}T${this.startTime}`);
                const end = new Date(`${this.endDate}T${this.endTime}`);
                this.$emit('change', [start.toISOString(), end.toISOString()]);
                this.$emit('input', [start.toISOString(), end.toISOString()]); // v-model
            } else {
                this.$emit('change', []);
                this.$emit('input', []);
            }
        },

        initializeFromValue() {
            if (Array.isArray(this.value) && this.value.length === 2) {
                const [start, end] = this.value.map(v => new Date(v));
                this.setFromDateObjects(start, end);
            }
        },

        // helper to set all fields from Date objects
        setFromDateObjects(start, end) {
            const pad = v => String(v).padStart(2, '0');
            const toDateStr = d => d.toISOString().substr(0, 10);
            const toTimeStr = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

            this.startDate = toDateStr(start);
            this.endDate = toDateStr(end);
            this.startTime = toTimeStr(start);
            this.endTime = toTimeStr(end);

            this.emitChange();
        },

        // QUICK RANGE PRESETS
        applyQuickRange(preset) {
            const now = new Date();
            let start;

            switch (preset) {
                case 'lastMonth':
                    // últimos 30 dias
                    start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
                case 'lastWeek':
                    // últimos 7 dias
                    start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case 'todayFull':
                    // hoje 00:00 até agora
                    start = new Date(now);
                    start.setHours(0, 0, 0, 0);
                    break;
                case 'lastHour':
                    start = new Date(now.getTime() - 1 * 60 * 60 * 1000);
                    break;
                case 'last30Minutes':
                    start = new Date(now.getTime() - 30 * 60 * 1000);
                    break;
                default:
                    return;
            }

            this.setFromDateObjects(start, now);
        },

        // your existing “Today” button (kept as-is)
        selectToday() {
            const today = new Date();
            const pad = (v) => String(v).padStart(2, '0');
            const dateStr = today.toISOString().substr(0, 10);
            const start = `${pad(today.getHours())}:${pad(today.getMinutes())}`;
            const endDate = new Date(today.getTime() + 30 * 60 * 1000); // +30min
            const end = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

            this.startDate = this.endDate = dateStr;
            this.startTime = start;
            this.endTime = end;

            this.emitChange();
        },

        clear() {
            this.startDate = '';
            this.endDate = '';
            this.startTime = '00:00';
            this.endTime = '00:00';

            this.emitChange();
        }
    },

    watch: {
        startDate: 'emitChange',
        endDate: 'emitChange',
        startTime: 'emitChange',
        endTime: 'emitChange',
        value: {
            handler: 'initializeFromValue',
            immediate: true
        }
    },

    mounted() {
        this.initializeFromValue();
    }
}
</script>
  
<style scoped lang="scss">
.datepickerContainer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .quick_ranges_row {
        display: inline-flex;
        align-items: center;
        justify-content: space-evenly;
        padding: 0.2rem 0.6rem;
        border-radius: 999px;
        border: 1px solid #b1791c;
        background: #4a320c;
        margin-bottom: 0.5rem;
        font-size: 0.85rem;
    }

    .quick_button {
        background: transparent;
        border: none;
        color: #e0c28a;
        padding: 0.2rem 0.6rem;
        cursor: pointer;
        white-space: nowrap;
        width: 100px;
        font-size: 1rem;
    }

    .quick_button+.quick_button {
        margin-left: 0.3rem;
    }

    .quick_button:hover {
        text-decoration: underline;
    }

    .filter_section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .filter_label {
        color: white;
        font-size: 0.85rem;
    }

    .filter_input {
        padding: 0.4rem;
        border-radius: 0.25rem;
        border: 1px solid #3b4655;
        background: #11161d;
        color: white;
    }

    .time_range_row {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
    }

    .time_input_group {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .button_row {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .utility_button {
        flex: 1;
        padding: 0.4rem;
        background: #334155;
        color: white;
        border: none;
        border-radius: 0.25rem;
        cursor: pointer;
        font-size: 0.85rem;
    }

    .utility_button:hover {
        background: #475569;
    }

    i {
        font-size: 0.95rem;
        color: #878f97;
        margin-top: 0.5rem;
    }
}
</style>
  